import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { translateAuthError } from "@/lib/authErrors";
import { toast } from "sonner";
import PublicShell from "@/components/PublicShell";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin(inviteCode) {
  if (inviteCode) localStorage.setItem("polla_invite_code", inviteCode.trim().toUpperCase());
  else localStorage.removeItem("polla_invite_code");
  const redirectUrl = window.location.origin + "/perfil";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (nickname.length < 3) throw new Error(t("register.errors.nicknameShort"));
      if (!inviteCode.trim()) throw new Error(t("register.errors.codeRequired"));
      const newUser = await register(email, password, nickname, inviteCode.trim().toUpperCase());
      toast.success(t("register.success"));
      navigate(newUser?.is_special_only ? "/pollas-especiales" : "/perfil");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(translateAuthError(msg, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicShell>
      <div className="max-w-xl mx-auto px-6 py-12">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">{t("register.kicker")}</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase leading-none mb-2">
          {t("register.titlePrefix")} <span className="text-[var(--c-lime)]">{t("register.titleHighlight")}</span>
        </h1>
        <p className="text-zinc-400 text-sm mt-3 mb-6">{t("register.subtitle")}</p>

        <div className="mb-4 p-3 border border-[var(--c-yellow)]/40 bg-[var(--c-yellow)]/5 text-[11px] text-[var(--c-yellow)]">
          {t("register.noCodeNotice")}
        </div>

        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-widest text-[var(--c-yellow)] mb-1">{t("register.codeLabel")}</label>
          <input
            data-testid="register-invite-code-input"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            className="w-full bg-zinc-950 border border-[var(--c-yellow)]/40 focus:border-[var(--c-yellow)] focus:ring-1 focus:ring-[var(--c-yellow)] outline-none px-3 py-3 rounded-sm font-mono-data uppercase tracking-wider"
            required
            placeholder={t("register.codePlaceholder")}
            maxLength={20}
            autoComplete="off"
          />
        </div>

        <button
          onClick={() => googleLogin(inviteCode)}
          data-testid="register-google-button"
          className="w-full mb-4 bg-white text-black font-bold uppercase tracking-wider py-3 hover:bg-zinc-100 transition-colors flex items-center justify-center gap-3"
        >
          <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615Z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"/></svg>
          {t("register.googleButton")}
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-zinc-700" />
          <div className="text-[11px] uppercase tracking-widest text-white font-semibold">{t("register.divider")}</div>
          <div className="flex-1 h-px bg-zinc-700" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white font-semibold mb-1.5">{t("register.nicknameLabel")}</label>
            <input
              data-testid="register-nickname-input"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-[var(--c-lime)] focus:ring-1 focus:ring-[var(--c-lime)] outline-none px-3 py-3 rounded-sm"
              required
              minLength={3}
              maxLength={20}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white font-semibold mb-1.5">{t("register.emailLabel")}</label>
            <input
              data-testid="register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-[var(--c-lime)] focus:ring-1 focus:ring-[var(--c-lime)] outline-none px-3 py-3 rounded-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white font-semibold mb-1.5">{t("register.passwordLabel")}</label>
            <input
              data-testid="register-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-[var(--c-lime)] focus:ring-1 focus:ring-[var(--c-lime)] outline-none px-3 py-3 rounded-sm"
              required
              minLength={4}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="register-submit-button"
            className="w-full bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider py-3 hover:bg-lime-300 transition-colors disabled:opacity-50"
          >
            {loading ? "..." : t("register.submit")}
          </button>
        </form>

        <div className="mt-6 text-sm text-zinc-400 text-center">
          {t("register.haveAccount")} <Link to="/login" data-testid="link-to-login" className="text-[var(--c-lime)] underline underline-offset-2">{t("register.signInLink")}</Link>
        </div>
      </div>
    </PublicShell>
  );
}
